import { Box, Drawer, Stack, Typography } from "@mui/material";
import { CoreButton } from "@verity/components/core";
import type { QuizResponse } from "@verity/database/prisma";
import { ReactNode, useState } from "react";
import apiClient from "../../../../../../helpers/api-client";
import i18n from "../../../../../../i18n";
import { VideoEvent } from "../../../../../utilities/useTimestamps";
import { BellIcon, IncorrectIcon } from "./assets";
import {
	isCollaborationDetected,
	type CollaborationFlaggedQuestion,
	type EssayCollaborationMatch,
} from "./collaborationEvents";
import styles from "./CollaborationDetectedEventRow.module.css";
import EssayFlaggedQuestion from "./EssayFlaggedQuestion";
import FlaggedQuestion from "./FlaggedQuestion";
import StudentCards from "./StudentCards";

type Timespan = {
	start: number;
	end: number;
};

type QuestionTimes = {
	[questionNo: number]: Timespan[];
};

type StudentType = {
	studentId?: number;
	studentName?: string;
	submitTime?: number;
	/** Absent for a partner only flagged via the essay-similarity flow, which doesn't track timing. */
	questionTimes?: QuestionTimes;
};

function getLastTimestampFromTimespans(timespans: Timespan[]): number {
	if (timespans.length === 0) return 0;
	return timespans.map((t) => t.end).sort((a, b) => a - b)[timespans.length - 1];
}

function calcOverlapPercentage(studentTimes: QuestionTimes = {}, partnerTimes: QuestionTimes = {}): number {
	let studentDuration = 0;
	let partnerDuration = 0;
	let overlapDuration = 0;

	const questionNumbers = new Set([...Object.keys(studentTimes), ...Object.keys(partnerTimes)]);

	for (const questionNo of questionNumbers) {
		const studentQuestionTimes = studentTimes[parseInt(questionNo)] || [];
		const partnerQuestionTimes = partnerTimes[parseInt(questionNo)] || [];

		studentDuration += studentQuestionTimes.reduce((sum, t) => sum + (t.end - t.start), 0);
		partnerDuration += partnerQuestionTimes.reduce((sum, t) => sum + (t.end - t.start), 0);

		for (const studentTimespan of studentQuestionTimes) {
			for (const partnerTimespan of partnerQuestionTimes) {
				if (studentTimespan.end > partnerTimespan.start && studentTimespan.start < partnerTimespan.end) {
					const overlapStart = Math.max(studentTimespan.start, partnerTimespan.start);
					const overlapEnd = Math.min(studentTimespan.end, partnerTimespan.end);
					overlapDuration += overlapEnd - overlapStart;
				}
			}
		}
	}

	const minDuration = Math.min(studentDuration, partnerDuration);
	if (minDuration <= 0) return 0;
	return Math.min(1, overlapDuration / minDuration);
}

function getStrongestScore(match: EssayCollaborationMatch) {
	const maxJaccard = Math.max(...match.chunks.map((chunk) => chunk.jaccard));
	const maxLevenshtein = Math.max(...match.chunks.map((chunk) => chunk.levenshtein));
	return Math.max(maxJaccard, maxLevenshtein);
}

const EVIDENCE_PAGE_SIZE = 5;

type CollaborationDetectedEventRowProps = {
	event: VideoEvent;
	resolveHandlers?: { sessionId: string; updateEvents: () => Promise<void> };
	readOnly?: boolean;
	open: boolean;
	setOpen: (open: boolean) => void;
};

export default function CollaborationDetectedEventRow({
	event,
	resolveHandlers,
	readOnly,
	open,
	setOpen,
}: CollaborationDetectedEventRowProps) {
	const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(false);
	const [isResolving, setIsResolving] = useState(false);

	const student = event.details.subject as StudentType;
	const partner = event.details.partner as StudentType;
	const questions = (event.details.questions || []) as CollaborationFlaggedQuestion[];
	const regularQuestions = questions.filter((question) => question.kind === "regular");
	const hasRegularQuestions = regularQuestions.length > 0;
	const essayQuestions = questions.filter(
		(question): question is Extract<CollaborationFlaggedQuestion, { kind: "essay" }> => question.kind === "essay",
	);

	const totalQuestions =
		(event.details.totalQuestions as number | undefined) ?? Object.keys(student.questionTimes ?? {}).length;

	const studentName = student.studentName ?? String(student.studentId);
	const partnerName = partner.studentName ?? String(partner.studentId);

	return (
		<>
			{i18n.t(
				"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.answerSimilarityDetected",
			)}
			<Drawer anchor="right" open={open} onClose={() => setOpen(false)} sx={{ zIndex: 1300 }}>
				<Box width={"600px"} height="100%" display="flex" flexDirection="column">
					<Box overflow="auto" flex={1} minHeight={0}>
						<Box className={styles.titleContainer}>
							<Stack direction="row" justifyContent="space-between">
								<Stack direction="row" gap="8px">
									<BellIcon />
									<Typography className={styles.title}>
										{i18n.t("result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.title")}
									</Typography>
								</Stack>
								<button
									type="button"
									aria-label={
										i18n.t(
											"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.close",
										) as string
									}
									onClick={() => setOpen(false)}
									className={styles.closeButton}
								>
									<IncorrectIcon height="24px" width="24px" color="#737373" />
								</button>
							</Stack>
						</Box>

						<Stack gap="14px" padding="20px">
							<StudentCards subjectStudentName={studentName} partnerStudentName={partnerName} />

							<Box className={styles.descriptionCard}>
								<Typography className={styles.descriptionText}>
									{hasRegularQuestions
										? i18n.t(
												"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.descriptionText",
												{
													studentName,
													partnerName,
													matchCount: regularQuestions.length,
													totalQuestions,
												},
											)
										: i18n.t(
												"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.descriptionTextEssay",
												{
													studentName,
													partnerName,
													matchCount: questions.length,
												},
											)}
								</Typography>
							</Box>

							<Stack direction="row" gap="10px" alignItems="center">
								<Box className={styles.statCard} flex={1}>
									<Typography className={styles.stat}>
										{questions.length}/{totalQuestions}
									</Typography>
									<Typography className={styles.statLabel}>
										{i18n.t(
											"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.matchingAnswersLabel",
										)}
									</Typography>
								</Box>
								{hasRegularQuestions ? (
									<Box className={styles.statCard} flex={1}>
										<Typography className={styles.stat}>
											{Math.round(calcOverlapPercentage(student.questionTimes, partner.questionTimes) * 100)}%
										</Typography>
										<Typography className={styles.statLabel}>
											{i18n.t(
												"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.overlapTimeLabel",
											)}
										</Typography>
									</Box>
								) : (
									(() => {
										const strongestScore =
											essayQuestions.length > 0
												? Math.max(...essayQuestions.map((question) => getStrongestScore(question.details)))
												: 0;
										const similarityPercent = Math.round(strongestScore * 100);
										return (
											<Box className={styles.statCard} flex={1}>
												<Typography className={styles.stat}>{similarityPercent}%</Typography>
												<Typography className={styles.statLabel}>
													{i18n.t(
														"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.essayMatchConfidenceLabel",
													)}
												</Typography>
											</Box>
										);
									})()
								)}
							</Stack>

							<Box height="1px" borderBottom="1px solid #E5E5E5" />

							<Stack direction="row" justifyContent="space-between" alignItems="center">
								<Typography className={styles.header}>
									{i18n.t(
										"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.supportingEvidence",
									)}
								</Typography>
								<Typography className={styles.flaggedCount}>
									{i18n.t(
										"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.flaggedQuestionsReviewed",
										{ count: questions.length },
									)}
								</Typography>
							</Stack>

							{questions
								.slice(0, isEvidenceExpanded ? questions.length : EVIDENCE_PAGE_SIZE)
								.map((question) =>
									question.kind === "essay" ? (
										<EssayFlaggedQuestion
											key={`essay-${question.details.questionId}-${question.details.subjectResponse.id}-${question.details.partnerResponse.id}`}
											studentName={studentName}
											partnerName={partnerName}
											match={question.details}
										/>
									) : (
										<FlaggedQuestion
											key={`regular-${question.details.questionNo}`}
											question={question.details as unknown as QuizResponse}
											submitGapTimeMs={Math.abs(
												getLastTimestampFromTimespans(
													(student.questionTimes ?? {})[question.details.questionNo] || [],
												) -
													getLastTimestampFromTimespans(
														(partner.questionTimes ?? {})[question.details.questionNo] || [],
													),
											)}
										/>
									),
								)}
							{questions.length > EVIDENCE_PAGE_SIZE && (
								<Box className={styles.viewMoreButton}>
									{isEvidenceExpanded ? (
										<Typography role="button" tabIndex={0} onClick={() => setIsEvidenceExpanded(false)}>
											{i18n.t(
												"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.viewLessFlaggedQuestions",
											)}
										</Typography>
									) : (
										<Typography role="button" tabIndex={0} onClick={() => setIsEvidenceExpanded(true)}>
											{i18n.t(
												"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.viewMoreFlaggedQuestions",
												{
													count: questions.length - (isEvidenceExpanded ? questions.length : EVIDENCE_PAGE_SIZE),
												},
											)}
										</Typography>
									)}
								</Box>
							)}
						</Stack>
					</Box>
					<Box className={styles.actionBar}>
						<CoreButton color="secondary" onClick={() => setOpen(false)}>
							{event.resolved
								? i18n.t("result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.close")
								: i18n.t("result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.cancel")}
						</CoreButton>
						{!event.resolved && !readOnly && (
							<CoreButton
								disabled={!resolveHandlers || !event.id || isResolving}
								onClick={async () => {
									if (!resolveHandlers || !event.id) return;
									setIsResolving(true);
									try {
										await apiClient.proctoringSession.updateEventFlag({
											proctoringSessionId: resolveHandlers.sessionId,
											eventId: event.id,
											resolved: true,
											flagLevel: 0,
										});
										await resolveHandlers.updateEvents();
										setOpen(false);
									} finally {
										setIsResolving(false);
									}
								}}
							>
								{i18n.t(
									"result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.markAsResolved",
								)}
							</CoreButton>
						)}
					</Box>
				</Box>
			</Drawer>
		</>
	);
}

export function useCollaborationRowAction(
	event: VideoEvent,
	resolveHandlers?: { sessionId: string; updateEvents: () => Promise<void> },
	readOnly?: boolean,
): {
	description: ReactNode;
	action: { label: string; active: boolean; onActivate: (e: React.MouseEvent) => void };
} | null {
	const [open, setOpen] = useState(false);

	if (!isCollaborationDetected(event)) {
		return null;
	}

	return {
		description: (
			<CollaborationDetectedEventRow
				event={event}
				resolveHandlers={resolveHandlers}
				open={open}
				setOpen={setOpen}
				readOnly={readOnly}
			/>
		),
		action: {
			label: event.resolved
				? (i18n.t("result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.review") as string)
				: (i18n.t("result.components.studentSelection.videoPlayer.collaborationDetectedEventRowTSX.resolve") as string),
			active: open,
			onActivate: (e) => {
				e.stopPropagation();
				setOpen(true);
			},
		},
	};
}
